export type ServerPowerAction = 'start' | 'stop' | 'restart' | 'kill'

export type ServerPowerFailure = {
  action?: ServerPowerAction
  billingRelated?: boolean
  title: string
  message: string
  reference?: string
  status?: number
  upstreamStatus?: number
  code?: string
}

// Only fixed, documented error identifiers may reach customer diagnostics.
// Arbitrary provider codes can contain secrets just like arbitrary messages.
const SAFE_CODES = new Set([
  'AccessDeniedHttpException', 'AuthenticationException', 'AuthorizationException',
  'DaemonConnectionException', 'DisplayException', 'ForbiddenHttpException',
  'HttpException', 'NotFoundHttpException', 'ServerStateConflictException',
  'TooManyRequestsHttpException', 'ValidationException', 'package_service_unavailable',
])

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === 'object' ? value as Record<string, unknown> : {}
}

function text(value: unknown): string {
  return typeof value === 'string' ? value.slice(0, 2048) : ''
}

/** Interpret known failure shapes, never echo provider payloads, paths or credentials. */
export function serverPowerFailure(error: unknown, action?: ServerPowerAction): ServerPowerFailure {
  if (error instanceof ServerPowerRequestError) return error.failure
  const source = record(error)
  const response = record(source.response)
  const data = record(response.data)
  const errors = Array.isArray(data.errors) ? data.errors.slice(0, 4).map(record) : []
  const details = [text(error), text(source.message), text(data.message), text(data.error),
    text(data.code), ...errors.flatMap(item => [text(item.code), text(item.detail)])].join(' ')
  const status = typeof response.status === 'number' ? response.status : undefined
  const code = [text(data.code), ...errors.map(item => text(item.code))].find(value => SAFE_CODES.has(value))
  const upstream = /\(code:\s*([45]\d{2})\)/i.exec(details)
  const upstreamStatus = upstream ? Number(upstream[1]) : undefined
  const uuid = '[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}'
  const reference = new RegExp(`(?:request_id:\\s*|Error Event \\[)(${uuid})`, 'i').exec(details)?.[1]
  const title = action ? `Failed to ${action} server` : 'Server command failed'
  let message: string
  if (/package_service_unavailable|package service authorization unavailable or denied|This service is not currently authorized\. Review its status in Metrly billing\./i.test(details)) {
    message = 'The server node denied runtime access. Review this server’s Billing page, or contact support if it should have access.'
  } else if (/suspended/i.test(details)) {
    message = 'This server is suspended. Review its Billing page or contact support before trying to start it.'
  } else if (upstreamStatus === 403) {
    message = 'The server node rejected this request (403). This is an access rejection, not a connection timeout. Contact support with the reference below.'
  } else if (upstreamStatus === 401) {
    message = 'The Console could not authenticate with the server node (401). Contact support to check the node connection.'
  } else if (upstreamStatus !== undefined && upstreamStatus >= 500) {
    message = `The server node reported an internal error (${upstreamStatus}). Open Console or contact support with the reference below.`
  } else if (status === 401) {
    message = 'Your session could not authorize this request. Sign in again and retry.'
  } else if (status === 403) {
    message = 'The server action was rejected (403). This response does not establish whether account permissions or server access caused the rejection. Contact support.'
  } else if (status === 409 || upstreamStatus === 409 || /another power action|power action.*being processed/i.test(details)) {
    message = 'Another server operation is in progress. Wait for it to finish, then try again.'
  } else if (status === 404) {
    message = 'This server is unavailable to this account. Refresh your server list or contact support.'
  } else if (status === 429) {
    message = 'Too many requests. Wait a moment before trying again.'
  } else if (status === 502 || status === 504 || /DaemonConnectionException/.test(details)) {
    message = 'The Console could not reach the server node. Try again, or contact support if the connection stays unavailable.'
  } else if (status === undefined && /ERR_NETWORK|ECONNABORTED|ETIMEDOUT/.test(text(source.code))) {
    message = 'The Console could not reach the server service. Check your connection and try again.'
  } else {
    message = 'The server could not complete this request. Open Console for more information, or contact support.'
  }
  if (!reference) message = message.replace(' with the reference below', '')
  const billingRelated = /package_service_unavailable|package service authorization unavailable or denied|This service is not currently authorized\. Review its status in Metrly billing\.|suspended/i.test(details)
  return { title, message, reference, status, upstreamStatus, code, action, billingRelated }
}

/** Clear only a failure whose requested outcome has since been observed live. */
export function isPowerFailureResolved(failure: ServerPowerFailure | null, state?: string | null): boolean {
  if (!failure?.action) return false
  // Running alone cannot prove a rejected restart actually restarted anything.
  if (failure.action === 'restart') return false
  const live = String(state || '').toLowerCase()
  return failure.action === 'start'
    ? live === 'running'
    : ['offline', 'stopped'].includes(live)
}

export class ServerPowerRequestError extends Error {
  readonly failure: ServerPowerFailure

  constructor(action: ServerPowerAction, error: unknown) {
    const failure = serverPowerFailure(error, action)
    super(`${failure.title}: ${failure.message}${failure.reference ? ` Reference: ${failure.reference}` : ''}`)
    this.name = 'ServerPowerRequestError'
    this.failure = failure
  }
}
